/* =========================
   Storage Keys
========================= */
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  USER: 'user',
  TOKEN_EXPIRY: 'tokenExpiry', // Store token expiry time for encrypted tokens
} as const;

/* =========================
   Cookie Utilities
========================= */

/**
 * Set a cookie with security flags
 * @param name - Cookie name
 * @param value - Cookie value
 * @param days - Expiration in days (omit for session cookie)
 */
const setCookie = (name: string, value: string, days?: number): void => {
  let expires = '';

  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }

  // Security flags: Secure (HTTPS only), SameSite=Lax for better compatibility
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const cookieString = `${name}=${encodeURIComponent(value)}${expires}; path=/; SameSite=Lax${secure}`;

  try {
    document.cookie = cookieString;

    // Verify cookie was set
    if (!getCookie(name)) {
      localStorage.setItem(`cookie_${name}`, value);
    }
  } catch (error) {
    console.error(`Error setting cookie ${name}:`, error);
    localStorage.setItem(`cookie_${name}`, value);
  }
};

/**
 * Get cookie value by name
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
const getCookie = (name: string): string | null => {
  try {
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1, cookie.length);
      }
      if (cookie.indexOf(nameEQ) === 0) {
        const value = cookie.substring(nameEQ.length, cookie.length);
        return decodeURIComponent(value);
      }
    }

    // Fallback to localStorage if cookie not found
    const fallbackValue = localStorage.getItem(`cookie_${name}`);
    return fallbackValue;
  } catch (error) {
    console.error(`Error getting cookie ${name}:`, error);
    return localStorage.getItem(`cookie_${name}`) || null;
  }
};

/**
 * Delete a cookie by name
 * @param name - Cookie name
 */
const deleteCookie = (name: string): void => {
  try {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax`;
    // Also clear localStorage fallback
    localStorage.removeItem(`cookie_${name}`);
  } catch (error) {
    console.error(`Error deleting cookie ${name}:`, error);
    localStorage.removeItem(`cookie_${name}`);
  }
};

/* =========================
   Types
========================= */
interface AuthData {
  accessToken?: string;
  refreshToken?: string;
  user: unknown;
  expiresIn?: number; // Access token lifetime in seconds
  refreshExpiresIn?: number; // Refresh token lifetime in seconds
  tokenExpiry?: number; // Token expiry timestamp in milliseconds
}

const DEFAULT_ACCESS_TOKEN_SECONDS = 15 * 60;
const DEFAULT_REFRESH_TOKEN_SECONDS = 12 * 60 * 60;

/* =========================
   Core Storage Functions
========================= */
export const setAuthData = (data: AuthData) => {
  try {
    console.log(
      '[Storage] Saving auth data - token:',
      data.accessToken ? 'present' : 'missing',
      ', user:',
      data.user ? 'present' : 'missing',
      ', expiresIn:',
      data.expiresIn,
      ', expiry:',
      data.tokenExpiry
    );

    // Access tokens are normally HttpOnly cookies set by the server. Keep
    // supporting a readable token for legacy callers without requiring it.
    if (data.accessToken) {
      setCookie(STORAGE_KEYS.ACCESS_TOKEN, data.accessToken);
    }

    // Refresh token: persistent cookie aligned with backend refresh lifetime
    if (data.refreshToken) {
      const refreshExpiresInSeconds =
        data.refreshExpiresIn ?? DEFAULT_REFRESH_TOKEN_SECONDS;
      setCookie(
        STORAGE_KEYS.REFRESH_TOKEN,
        data.refreshToken,
        refreshExpiresInSeconds / 86400
      );
    }

    // Store user data in localStorage (persisted across sessions)
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));

    if (data.accessToken) {
      const resolvedTokenExpiry =
        data.tokenExpiry ??
        (data.expiresIn
          ? Date.now() + data.expiresIn * 1000
          : Date.now() + DEFAULT_ACCESS_TOKEN_SECONDS * 1000);

      localStorage.setItem(
        STORAGE_KEYS.TOKEN_EXPIRY,
        resolvedTokenExpiry.toString()
      );
      console.log(
        '[Storage] Token expiry stored:',
        new Date(resolvedTokenExpiry).toISOString()
      );
    }

    console.log('[Storage] Auth data saved successfully');
  } catch (error) {
    console.error('Error setting auth data:', error);
  }
};

export const clearAuthData = () => {
  try {
    // Clear cookies
    deleteCookie(STORAGE_KEYS.ACCESS_TOKEN);
    deleteCookie(STORAGE_KEYS.REFRESH_TOKEN);

    // Clear user data and token expiry from localStorage
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY);
  } catch (error) {
    console.error('Error clearing auth data:', error);
  }
};

/**
 * Update stored user data in localStorage
 * Useful for updating user info after fetching from /api/auth/user/me
 */
export const updateStoredUser = (user: Record<string, unknown>) => {
  try {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  } catch (error) {
    console.error('Error updating stored user:', error);
  }
};

/* =========================
   Token Getters
========================= */
export const getAccessToken = (): string | null => {
  try {
    return getCookie(STORAGE_KEYS.ACCESS_TOKEN);
  } catch (error) {
    console.error('Error getting access token:', error);
    return null;
  }
};

export const getRefreshToken = (): string | null => {
  try {
    return getCookie(STORAGE_KEYS.REFRESH_TOKEN);
  } catch (error) {
    console.error('Error getting refresh token:', error);
    return null;
  }
};

export const getUser = <T = unknown>(): T | null => {
  try {
    const userStr = localStorage.getItem(STORAGE_KEYS.USER);
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
};

/* =========================
   Token Validation
========================= */

/**
 * Check if token is expired
 * For encrypted tokens, uses stored expiry time (no decoding)
 * @param token - The access token
 * @param buffer - Extra time buffer in seconds (default: 30s)
 */
export const isTokenExpired = (token: string, buffer: number = 30): boolean => {
  try {
    // Get stored token expiry time (set at login time)
    const storedExpiry = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);

    if (!storedExpiry) {
      console.log('[Storage] No stored token expiry found - assuming expired');
      return true; // No expiry stored = expired
    }

    const expiryTime = parseInt(storedExpiry, 10);
    const currentTime = Date.now();
    const bufferTime = buffer * 1000;
    const isExpired = currentTime >= expiryTime - bufferTime;

    console.log(
      '[Storage] Token expiry check - expires at:',
      new Date(expiryTime).toISOString(),
      'current:',
      new Date(currentTime).toISOString(),
      'expired?',
      isExpired
    );
    return isExpired;
  } catch (error) {
    console.error('Error checking token expiry:', error);
    return true; // On error, assume expired
  }
};

/**
 * Check if user is authenticated with valid token
 */
export const isAuthenticated = (): boolean => {
  if (!getUser()) return false;

  const token = getAccessToken();
  return token ? !isTokenExpired(token) : true;
};

/* =========================
   Token Update
========================= */

/**
 * Update only the access token (useful for refresh flows)
 */
export const updateAccessToken = (accessToken: string): void => {
  try {
    setCookie(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
  } catch (error) {
    console.error('Error updating access token:', error);
  }
};

/**
 * Update stored access token expiry after refresh (seconds from now).
 */
export const updateTokenExpiry = (expiresInSeconds: number): void => {
  try {
    const tokenExpiry = Date.now() + expiresInSeconds * 1000;
    localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, tokenExpiry.toString());
  } catch (error) {
    console.error('Error updating token expiry:', error);
  }
};
